import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatRupiah, priceInfo } from "@/lib/api";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ArrowRight, Palette, Search } from "lucide-react";
import { LazyImg } from "@/components/LazyMedia";

const PER_PAGE = 12;

export default function Catalog() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { setPage(1); }, [q]);

  const filtered = useMemo(() => {
    if (!q) return products;
    const s = q.toLowerCase();
    return products.filter((p) => (p.name || "").toLowerCase().includes(s) || (p.description || "").toLowerCase().includes(s));
  }, [products, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, totalPages);
  const pageItems = filtered.slice((current - 1) * PER_PAGE, current * PER_PAGE);

  return (
    <div className="App">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-10">
          <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">Katalog Produk</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#00133F]">Pilih & kustomisasi</h1>
          {q ? (
            <p className="text-slate-600 mt-3">Hasil pencarian untuk <span className="font-semibold text-[#00133F]">"{q}"</span> — {filtered.length} produk.
              <button onClick={() => setParams({})} className="ml-2 text-[#F0005A] font-medium hover:underline" data-testid="clear-search">Hapus filter</button>
            </p>
          ) : (
            <p className="text-slate-600 mt-3 max-w-lg">Klik produk untuk memilih varian dan mengunggah desain (opsional).</p>
          )}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => <div key={i} className="rounded-2xl h-96 bg-slate-100 animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 rounded-2xl border border-dashed border-slate-300" data-testid="catalog-empty">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-5"><Search className="w-7 h-7 text-slate-400" /></div>
            <p className="text-slate-600 font-medium">Produk tidak ditemukan{q ? ` untuk "${q}"` : ""}.</p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="catalog-grid">
              {pageItems.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % PER_PAGE) * 0.05 }}>
                  <div data-testid={`catalog-product-${p.id}`}
                    className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:shadow-[0_20px_50px_rgb(10,25,47,0.1)] hover:-translate-y-1 transition-all duration-300 flex flex-col h-full">
                    <Link to={`/kustom/${p.id}`} data-testid={`catalog-image-link-${p.id}`} className="aspect-square overflow-hidden bg-slate-50 relative block">
                      {p.best_seller && (
                        <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1 bg-[#F0005A] text-white text-[10px] font-bold px-2 py-1 rounded-full">BEST SELLER</span>
                      )}
                      <LazyImg src={p.image_url} alt={p.name} className="w-full h-full object-contain p-3 group-hover:scale-105 transition-[transform,opacity] duration-500" />
                    </Link>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-heading text-xl font-semibold text-[#00133F]">{p.name}</h3>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-lg font-bold text-[#F0005A]">{priceInfo(p).from ? "mulai " : ""}{formatRupiah(priceInfo(p).effective)}</span>
                          {priceInfo(p).promo != null && <span className="text-sm text-slate-400 line-through">{formatRupiah(p.base_price)}</span>}
                          {priceInfo(p).discount > 0 && <span className="text-[10px] font-bold text-[#F0005A] bg-[#F0005A]/10 px-1.5 py-0.5 rounded-full">Hemat {priceInfo(p).discount}%</span>}
                          {p.preorder && <span className="text-[10px] font-bold text-[#00133F] bg-slate-100 px-1.5 py-0.5 rounded-full">PO</span>}
                        </div>
                        <span className="text-xs text-slate-400">/ pcs</span>
                      </div>
                      <Link to={`/kustom/${p.id}`} className="mt-auto pt-4">
                        <Button data-testid={`customize-btn-${p.id}`} className="w-full rounded-full bg-[#00133F] hover:bg-[#00133F]/90 text-white">
                          <Palette className="w-4 h-4 mr-2" /> Kustomisasi <ArrowRight className="w-4 h-4 ml-auto" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12" data-testid="catalog-pagination">
                <button onClick={() => setPage(Math.max(1, current - 1))} disabled={current === 1}
                  className="px-3 h-9 rounded-full border border-slate-200 text-sm disabled:opacity-40 hover:border-[#F0005A]" data-testid="page-prev">Sebelumnya</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <button key={n} onClick={() => setPage(n)} data-testid={`page-${n}`}
                    className={`w-9 h-9 rounded-full text-sm font-medium transition ${n === current ? "bg-[#F0005A] text-white" : "border border-slate-200 text-slate-600 hover:border-[#F0005A]"}`}>{n}</button>
                ))}
                <button onClick={() => setPage(Math.min(totalPages, current + 1))} disabled={current === totalPages}
                  className="px-3 h-9 rounded-full border border-slate-200 text-sm disabled:opacity-40 hover:border-[#F0005A]" data-testid="page-next">Berikutnya</button>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
